import { useEffect, useRef, useState } from "react";

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { useHighlightMatch } from "~/hooks/useHighlightMatch";

import { Document, Folder } from "~/api/model";
import { trackEvent } from "~/utils/analytics";
import { ShowMoreResults } from "./ShowMoreResults";
import { TypeHeader } from "./TypeHeader";

type AllResultsProps = {
  data: { documents: Document[]; cases: Folder[] };
  goToCase: (id: string) => void;
  query: string;
};

const ALL_RESULTS_LIMIT = 10;

export const AllResults: React.FC<AllResultsProps> = ({
  data,
  goToCase,
  query,
}) => {
  const commandRef = useRef<HTMLDivElement>(null);

  const [activeBadge, setActiveBadge] = useState("all");
  const [documentResults, setDocumentResults] = useState(data.documents);
  const [caseResults, setCaseResults] = useState(data.cases);

  const highlightMatch = useHighlightMatch();

  useEffect(() => {
    commandRef.current?.focus();
  }, [documentResults, caseResults]);

  useEffect(() => {
    if (activeBadge === "all") {
      setDocumentResults(data.documents.slice(0, ALL_RESULTS_LIMIT));
      setCaseResults(data.cases.slice(0, ALL_RESULTS_LIMIT));
    } else {
      setDocumentResults(data.documents);
      setCaseResults(data.cases);
    }
  }, [activeBadge]);

  const handleShowMore = (entity: "documents" | "cases") => {
    trackEvent({ eventName: "app/search_modal/filter results.link/click" });
    setActiveBadge(entity);
  };

  return (
    <div className="pb-3 overflow-y-scroll flex flex-col gap-4">
      <Command
        className="focus:border-0 focus-visible:outline-none"
        ref={commandRef}
      >
        <CommandList className="max-h-[450px]">
          {["all", "cases"].includes(activeBadge) && !!caseResults.length && (
            <>
              <TypeHeader
                activeBadge={activeBadge}
                type={"cases"}
                clearType={() => setActiveBadge("all")}
              />
              <CommandGroup>
                {caseResults.map((caseItem: Folder) => (
                  <CommandItem
                    value={caseItem.id}
                    key={caseItem.id}
                    className="cursor-pointer"
                    onSelect={() => goToCase(caseItem.id)}
                    tabIndex={0}
                  >
                    <span>{highlightMatch(caseItem.name, query)}</span>
                  </CommandItem>
                ))}
                {data.cases.length > ALL_RESULTS_LIMIT &&
                  activeBadge === "all" && (
                    <ShowMoreResults
                      type="cases"
                      handleClick={() => handleShowMore("cases")}
                    />
                  )}
              </CommandGroup>
            </>
          )}
          {["all", "documents"].includes(activeBadge) &&
            !!documentResults.length && (
              <>
                <TypeHeader
                  activeBadge={activeBadge}
                  type={"documents"}
                  clearType={() => setActiveBadge("all")}
                />
                <CommandGroup>
                  {documentResults.map((docItem: Document) => (
                    <CommandItem
                      value={`${docItem.id}:${docItem.folderId}`}
                      key={`${docItem.id}:${docItem.folderId}`}
                      className="flex gap-2 cursor-pointer"
                      onSelect={() => {
                        trackEvent({
                          eventName: "app/search_modal/result.link/click",
                        });
                        goToCase(docItem.folderId);
                      }}
                      tabIndex={0}
                    >
                      <span className="truncate max-w-[450px]">
                        {highlightMatch(docItem.filename, query)}
                      </span>
                      <span className="text-muted-foreground truncate max-w-[200px]">
                        {docItem.folder?.name}
                      </span>
                    </CommandItem>
                  ))}
                  {data.documents.length > ALL_RESULTS_LIMIT &&
                    activeBadge === "all" && (
                      <ShowMoreResults
                        type="documents"
                        handleClick={() => handleShowMore("documents")}
                      />
                    )}
                </CommandGroup>
              </>
            )}
        </CommandList>
      </Command>
    </div>
  );
};
